import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { FormHelpers, FormValues } from './types';

interface FormContextProviderProps {
	initialValues: FormValues;
	formHelpers: FormHelpers;
	children: ReactNode;
}

interface FormContextValue {
	initialValues: FormValues;
	formHelpers: Omit<FormHelpers, 'children'>;
}

interface FormStaticContextValue {
	readOnly: boolean;
	fieldLabels?: Map<string, string>;
	requiredFields?: string[];
	initialValues: FormValues;
}

const FormStaticContext = createContext<FormStaticContextValue | undefined>(undefined);
export const FormContext = createContext<FormContextValue | undefined>(undefined);

export function FormContextProvider({ initialValues, formHelpers, children }: FormContextProviderProps) {
	const staticValue = useMemo<FormStaticContextValue>(
		() => ({
			readOnly: formHelpers.readOnly,
			fieldLabels: formHelpers.fieldLabels,
			requiredFields: formHelpers.requiredFields,
			initialValues
		}),
		[formHelpers.readOnly, formHelpers.fieldLabels, formHelpers.requiredFields, initialValues]
	);

	return (
		<FormStaticContext.Provider value={staticValue}>
			<FormContext.Provider value={{ initialValues, formHelpers }}>{children}</FormContext.Provider>
		</FormStaticContext.Provider>
	);
}

export function useFormStatic() {
	const context = useContext(FormStaticContext);
	if (!context) {
		throw new Error('useFormStatic must be used within a FormContextProvider');
	}
	return context;
}

export function useForm() {
	const context = useContext(FormContext);
	if (!context) {
		throw new Error('useForm must be used within a FormContextProvider');
	}
	return context;
}
